import { useEffect, useRef, useState, useCallback } from 'react'
import { useCallStore } from '../stores/callStore'
import type { ExtractedEntities, ProfileField, ProfileFields } from '../types'

const API_URL = (import.meta.env.VITE_API_URL as string) || 'http://localhost:8787'
const WS_URL = (import.meta.env.VITE_WS_URL as string) || 'ws://localhost:8787'

interface WSMessage {
  type: 'transcript' | 'entities' | 'call_started' | 'call_ended' | 'error'
  data: unknown
}

export function useWebSocket(callSid?: string) {
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const maxReconnectAttempts = 5
  
  const { addTranscript, updateField, startCall, endCall, setDetectedIntent } = useCallStore()

  const connect = useCallback(() => {
    if (!callSid) return

    const wsUrl = `${WS_URL}/ws/call/${callSid}`
    console.log('Connecting to WebSocket:', wsUrl)

    try {
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        console.log('WebSocket connected')
        setIsConnected(true)
        setError(null)
        reconnectAttemptsRef.current = 0
      }

      ws.onmessage = (event) => {
        console.log('WebSocket message received:', event.data)
        try {
          const message: WSMessage = JSON.parse(event.data)
          console.log('Parsed message:', message.type, message.data)
          handleMessage(message)
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err)
        }
      }

      ws.onerror = (err) => {
        console.error('WebSocket error:', err)
        setError('Connection error')
        setIsConnected(false)
      }

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason)
        setIsConnected(false)

        // Attempt reconnection if not intentionally closed
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000)
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`)
          setTimeout(connect, delay)
        }
      }
    } catch (err) {
      console.error('Failed to create WebSocket:', err)
      setError('Failed to connect')
    }
  }, [callSid])

  const handleMessage = useCallback((message: WSMessage) => {
    switch (message.type) {
      case 'call_started':
        console.log('Call started:', message.data)
        startCall()
        break

      case 'call_ended':
        console.log('Call ended:', message.data)
        endCall()
        break

      case 'transcript':
        const transcript = message.data as { text: string; speaker: 'customer' | 'stylist'; timestamp: number }
        addTranscript(transcript)
        break

      case 'entities':
        // Skip entity extraction when AI is paused (but transcript still flows)
        if (useCallStore.getState().isAIPaused) {
          console.log('AI paused - skipping entity extraction')
          break
        }
        const entities = message.data as ExtractedEntities
        updateEntitiesFromExtraction(entities)
        break

      case 'error':
        const errorData = message.data as { error: string }
        setError(errorData.error)
        break

      default:
        console.log('Unknown message type:', message.type)
    }
  }, [addTranscript, startCall, endCall])

  const updateEntitiesFromExtraction = useCallback((entities: ExtractedEntities) => {
    const currentFields = useCallStore.getState().fields
    
    // Fields that should be replaced (single value)
    const singleValueFields: Array<{ 
      key: keyof ExtractedEntities; 
      fieldName: keyof typeof entities.confidence 
    }> = [
      { key: 'firstName', fieldName: 'firstName' },
      { key: 'lastName', fieldName: 'lastName' },
      { key: 'phone', fieldName: 'phone' },
      { key: 'email', fieldName: 'email' },
      { key: 'date', fieldName: 'date' },
      { key: 'time', fieldName: 'time' },
      { key: 'requestedStylist', fieldName: 'requestedStylist' }
    ]

    // Handle SERVICE separately - accumulate instead of replace
    if (entities.service) {
      const newService = entities.service
      const confidence = entities.confidence?.service || 0
      const currentServices = currentFields.service?.value || ''
      
      // Parse existing services into array
      const existingServices = currentServices
        .split(',')
        .map(s => s.trim().toLowerCase())
        .filter(Boolean)
      
      // Parse new service(s) - could be comma-separated from AI
      const newServices = newService
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
      
      // Add only services that aren't already in the list
      const servicesToAdd: string[] = []
      newServices.forEach(service => {
        const serviceLower = service.toLowerCase()
        const alreadyExists = existingServices.some(existing => 
          existing === serviceLower || 
          existing.includes(serviceLower) || 
          serviceLower.includes(existing)
        )
        if (!alreadyExists) {
          servicesToAdd.push(service)
        }
      })
      
      if (servicesToAdd.length > 0) {
        // Combine existing and new services
        const allServices = currentServices 
          ? `${currentServices}, ${servicesToAdd.join(', ')}`
          : servicesToAdd.join(', ')
        
        console.log(`Adding services: ${servicesToAdd.join(', ')} -> Total: ${allServices}`)
        
        updateField('service', {
          value: allServices,
          confidence: Math.max(confidence, currentFields.service?.confidence || 0),
          status: 'confirmed',
          updatedAt: Date.now()
        })
      } else {
        console.log(`Service "${newService}" already in list, skipping`)
      }
    }

    // Handle single-value fields
    singleValueFields.forEach(({ key, fieldName }) => {
      const value = entities[key]
      if (value === null || value === undefined) return

      const confidence = entities.confidence?.[fieldName] || 0
      const currentField = currentFields[key as keyof typeof currentFields]
      
      // PROTECTION: Don't overwrite confirmed fields with lower confidence data
      // This prevents the hairdresser's confirmation speech from changing the form
      if (currentField?.status === 'confirmed' && currentField?.value) {
        // Only update if new value has HIGHER confidence AND is different
        // Or if the new value is genuinely new information (not just rephrased)
        const isHigherConfidence = confidence > (currentField.confidence || 0)
        const isSameValue = currentField.value?.toLowerCase().trim() === (value as string)?.toLowerCase().trim()
        
        if (isSameValue) {
          // Same value - skip (no need to update)
          console.log(`Skipping ${key}: same value already confirmed`)
          return
        }
        
        if (!isHigherConfidence) {
          // Lower confidence trying to overwrite - skip
          console.log(`Skipping ${key}: confirmed field won't be overwritten by lower confidence (${confidence} < ${currentField.confidence})`)
          return
        }
      }
      
      // Determine status based on confidence
      // 80+ = confirmed (green)
      // 50-79 = mentioned (yellow)
      // <50 = warning (amber)
      let status: ProfileField['status'] = 'mentioned'
      if (confidence >= 80) status = 'confirmed'
      else if (confidence < 50) status = 'warning'

      updateField(key as keyof ProfileFields, {
        value: value as string,
        confidence,
        status,
        updatedAt: Date.now()
      })
    })

    // Handle notes separately - smart itemization, no duplicates
    if (entities.notes) {
      const currentNotes = currentFields.notes?.value || ''
      const newNoteText = entities.notes.trim()
      
      // Parse existing notes into individual items (split by bullet or newline)
      const existingItems = currentNotes
        .split(/[•\n]/)
        .map(s => s.trim())
        .filter(Boolean)
      
      // Extract key phrases from the new note for deduplication
      const newNoteLower = newNoteText.toLowerCase()
      
      // Check if this note (or its key content) already exists
      const isDuplicate = existingItems.some(existing => {
        const existingLower = existing.toLowerCase()
        // Check for substantial overlap (more than just a few words)
        return existingLower.includes(newNoteLower) || 
               newNoteLower.includes(existingLower) ||
               // Check for key phrase matches (dates, events)
               (newNoteLower.includes('birthday') && existingLower.includes('birthday')) ||
               (newNoteLower.includes('holiday') && existingLower.includes('holiday')) ||
               (newNoteLower.includes('pregnant') && existingLower.includes('pregnant')) ||
               (newNoteLower.includes('baby') && existingLower.includes('baby')) ||
               (newNoteLower.includes('wedding') && existingLower.includes('wedding')) ||
               (newNoteLower.includes('anniversary') && existingLower.includes('anniversary'))
      })
      
      if (!isDuplicate && newNoteText.length > 3) {
        // Add as new bullet point
        const updatedNotes = existingItems.length > 0
          ? `${existingItems.map(item => `• ${item}`).join('\n')}\n• ${newNoteText}`
          : `• ${newNoteText}`
        
        console.log(`Adding note: "${newNoteText}"`)
        
        updateField('notes', {
          value: updatedNotes,
          confidence: 100,
          status: 'confirmed',
          updatedAt: Date.now()
        })
      } else {
        console.log(`Skipping duplicate note: "${newNoteText}"`)
      }
    }
    
    // Handle intent detection - trigger cancellation modal if needed
    if (entities.intent) {
      setDetectedIntent(entities.intent)
    }
  }, [updateField, setDetectedIntent])

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect')
      wsRef.current = null
    }
    setIsConnected(false)
  }, [])

  // Connect when callSid changes
  useEffect(() => {
    if (callSid) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, [callSid, connect, disconnect])

  return { 
    isConnected, 
    error, 
    connect, 
    disconnect 
  }
}

// API helper functions
export async function createCustomer(data: {
  firstName: string
  lastName?: string
  phone: string
  email?: string
  notes?: string
}) {
  const response = await fetch(`${API_URL}/api/customers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })
  return response.json()
}

export async function createAppointment(data: {
  customerId: string
  service: string
  date: string
  time: string
  notes?: string
}) {
  const response = await fetch(`${API_URL}/api/appointments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })
  return response.json()
}

export async function findCustomerByPhone(phone: string) {
  const response = await fetch(`${API_URL}/api/customers/phone/${encodeURIComponent(phone)}`)
  return response.json()
}
